import { useState } from 'react';
import { t } from '@bms2/shared';
import { NoteList } from '../../components/composites/Tile';
import { useDateFormat } from '../../shell/ViewerZone';
import { RecordNoteModal } from './RecordNoteModal';
import { noteDraftFrom, noteItemOf, type RowActions, type TileRow } from './tileItems';
import styles from './DealSectionNotes.module.css';

/** The two writes a note's thread makes and the threads held open, from the page, which outlives the reload a reply causes (decision 0671). */
export type SectionNoteReplies = Pick<RowActions, 'onNoteReply' | 'onNoteReplyDelete' | 'openThreads' | 'onToggleThread'>;

export interface DealSectionNotesProps {
  dealId: string;
  /** The row's sub-stage key, which is the notes' section key. */
  subStage: string;
  /** The row's tagged notes, newest first, in the Internal Notes row shape. */
  notes: readonly TileRow[];
  /** The viewer, for the pencil that only a note's own author is offered (decision 0301). */
  personId?: string;
  /** The viewer's zone, so an instant reads in their own day (decision 0261). */
  timeZone?: string;
  apiBaseUrl?: string;
  replies?: SectionNoteReplies;
  onChanged: () => void;
  onError?: (message: string) => void;
}

/**
 * 🔒 A stage-card row's notes (decision 0725, as amended by 0950): every note,
 * newest first, drawn by the Internal Notes tile's own list — three notes tall and
 * scrolling inside for the rest, each note's author and time in the viewer's zone
 * above its text, each edited by its author through the note window and answered
 * with replies exactly as Internal Notes answers them (decision 0671).
 *
 * 🔨 The composer that adds a note is the row's own, drawn by the card above
 * this (decision 0910); nothing here writes a note, so there is one way in.
 *
 * 🔨 The row carries no section pill of its own: the row it stands in already
 * names the section, and the Deal's Internal Notes tile is where the pill is read.
 */
export function DealSectionNotes({ dealId, subStage, notes, personId = '', timeZone = 'UTC', apiBaseUrl = '', replies = {}, onChanged, onError }: DealSectionNotesProps) {
  const dateFormat = useDateFormat();
  /** The note whose author opened its pencil; the window is keyed to it and unmounted when closed. */
  const [editing, setEditing] = useState<TileRow | null>(null);
  if (notes.length === 0) return null;
  const actions: RowActions = {
    ...replies,
    onRowEdit: (row) => setEditing(row),
    rowEditLabel: t('note.edit'),
    rowEditWhen: (row) => Boolean(personId) && row.raw?.authorPersonId === personId,
  };
  const items = notes.map((row) => ({ ...noteItemOf(row, timeZone, actions, dateFormat), section: null }));
  return (
    <div className={styles.block} data-section-notes={subStage} data-section-note-count={notes.length}>
      <NoteList items={items} name={subStage} className={styles.list} />
      {/* 🔨 Keyed to the note it edits and unmounted when closed: a window held open behind a flag reads its first note's draft on the second. */}
      {editing ? (
        <RecordNoteModal
          key={editing.id ?? 'note'}
          open
          recordType="Deal"
          recordId={dealId}
          apiBaseUrl={apiBaseUrl}
          noteId={editing.id ?? null}
          initial={noteDraftFrom(editing)}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
          {...(onError ? { onError } : {})}
        />
      ) : null}
    </div>
  );
}
